import React, { useEffect } from 'react';
import { Tag, message } from 'antd';
import GlobalTable from '@/components/GlobalTable';
import { useTable } from '@/hooks/useTable';
import type { TableColumn } from '@/types/table';
import { usersAPI } from '@/services/api/users';
import type { AuditLog } from '@/types/api';
import dayjs from 'dayjs';

// 将后端 action 映射到前端 operationType
const actionToOperationType = (action: string): string => {
  // 固件版本相关
  if (action === 'fw_version_create') return 'create_version';
  if (action === 'fw_version_publish') return 'release';
  if (action === 'fw_version_pause') return 'pause';
  if (action === 'fw_version_resume') return 'release';
  if (action === 'fw_version_update') return 'modify_version';
  if (action === 'fw_version_set_test_devices') return 'modify_version';
  
  // App版本相关
  if (action === 'app_version_create') return 'create_version';
  if (action === 'app_version_publish') return 'release';
  if (action === 'app_version_pause') return 'pause';
  if (action === 'app_version_resume') return 'release';
  if (action === 'app_version_update') return 'modify_version';
  if (action === 'app_version_delete') return 'delete_version';
  
  // 用户相关
  if (action === 'user_create') return 'create_user';
  if (action === 'user_update') return 'modify_user';
  if (action === 'user_delete') return 'delete_user';
  
  // 策略相关
  if (action === 'fw_strategy_create') return 'modify_policy';
  if (action === 'fw_strategy_update') return 'modify_policy';
  if (action === 'fw_strategy_delete') return 'modify_policy';
  if (action === 'fw_rollout_advance') return 'modify_policy';
  if (action === 'fw_rollout_clear_manual') return 'modify_policy';
  
  // 文件相关
  if (action === 'file_upload') return 'upload_file';
  if (action === 'file_delete') return 'delete_file';
  
  // 机型相关
  if (action === 'device_model_create') return 'create_model';
  if (action === 'device_model_update') return 'modify_model';
  if (action === 'device_model_delete') return 'delete_model';
  
  // 系统设置
  if (action === 'system_settings_update') return 'modify_settings';
  
  // 默认返回原 action
  return action;
};

// 操作类型配置
const operationTypeConfig: Record<string, { label: string; color: string }> = {
  create_version: { label: '创建版本', color: 'blue' },
  release: { label: '发布', color: 'green' },
  pause: { label: '暂停', color: 'orange' },
  rollback: { label: '回滚', color: 'red' },
  create_user: { label: '创建用户', color: 'purple' },
  modify_user: { label: '修改用户', color: 'gold' },
  delete_user: { label: '删除用户', color: 'red' },
  delete_version: { label: '删除版本', color: 'red' },
  modify_version: { label: '修改版本', color: 'cyan' },
  modify_policy: { label: '修改策略', color: 'purple' },
  upload_file: { label: '上传文件', color: 'blue' },
  delete_file: { label: '删除文件', color: 'red' },
  create_model: { label: '创建机型', color: 'blue' },
  modify_model: { label: '修改机型', color: 'cyan' },
  delete_model: { label: '删除机型', color: 'red' },
  modify_settings: { label: '修改设置', color: 'purple' },
};

// 前端显示的数据结构
interface SystemLogDisplay {
  id: string;
  operationType: string;
  operator: string;
  timestamp: string;
  details: string;
  ipAddress: string;
}

const SystemLog: React.FC = () => {
  const { tableRef, loading, setLoading, dataSource, setDataSource, pagination, updatePagination } = useTable<SystemLogDisplay>();

  // 获取审计日志
  const fetchAuditLogs = async (page: number = 1, pageSize: number = 20, search: string = '') => {
    setLoading(true);
    try {
      const params: any = {
        page,
        size: pageSize,
      };

      // 如果搜索不为空，使用 username 参数进行搜索
      if (search.trim()) {
        params.username = search.trim();
      }

      const response = await usersAPI.getAuditLogs(params);
      
      // 转换数据格式
      const logs: SystemLogDisplay[] = (response.list || []).map((log: AuditLog) => {
        const operationType = actionToOperationType(log.action);
        return {
          id: log.id.toString(),
          operationType,
          operator: log.username,
          timestamp: dayjs(log.createdAt).format('YYYY-MM-DD HH:mm:ss'),
          details: log.detail,
          ipAddress: log.ip,
        };
      });

      setDataSource(logs);
      updatePagination(page, pageSize, response.total);
    } catch (error: any) {
      console.error('获取审计日志失败:', error);
      message.error(error?.message || '获取审计日志失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化数据
  useEffect(() => {
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 处理搜索
  const handleSearch = (value: string) => {
    fetchAuditLogs(1, pagination.pageSize || 20, value);
  };

  // 处理分页变化
  const handlePaginationChange = (page: number, pageSize: number) => {
    fetchAuditLogs(page, pageSize);
  };

  // 表格列配置
  const columns: TableColumn<SystemLogDisplay>[] = [
    {
      title: '操作类型',
      dataIndex: 'operationType',
      key: 'operationType',
      width: 120,
      render: (value: string) => {
        const config = operationTypeConfig[value] || { label: value, color: 'default' };
        return (
          <Tag color={config.color}>
            {config.label}
          </Tag>
        );
      },
    },
    {
      title: '操作人',
      dataIndex: 'operator',
      key: 'operator',
      width: 100,
    },
    {
      title: '时间',
      dataIndex: 'timestamp',
      key: 'timestamp',
      width: 180,
    },
    {
      title: '详情',
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
    },
    {
      title: 'IP地址',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 140,
    },
  ];

  return (
    <div className="p-6">
      <GlobalTable
        ref={tableRef}
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        pagination={{
          ...pagination,
          onChange: handlePaginationChange,
          showTotal: true,
        }}
        header={{
          title: '系统日志',
          subtitle: '查看用户操作记录和系统活动日志',
        }}
        toolbar={{
          
          showSearch: true,
          searchPlaceholder: '搜索操作记录...',
          onSearch: handleSearch,
        }}
        rowKey="id"
        size="middle"
      />
    </div>
  );
};

export default SystemLog;
